<div class="container mx-auto px-4 py-8 prose prose-gray dark:prose-invert max-w-3xl">
  <h1>Privacy Policy</h1>
  <p class="text-sm text-gray-500 dark:text-gray-400">Last updated: May 14, 2025</p>

  <p>
    At Summerry.com, we respect your privacy and are committed to protecting your personal data. This Privacy Policy
    explains how we collect, use, and store your information when you visit our website or subscribe to our newsletter.
  </p>

  <h2>1. Who We Are</h2>
  <p>
    This website is operated by Summerry. We provide automated AI news digests focused on artificial intelligence and
    machine learning topics, using trusted sources.
  </p>

  <h2>2. What Information We Collect</h2>
  <p>We collect only the minimum amount of data needed to provide and improve our service.</p>

  <h3>a. Information You Provide</h3>
  <p>Email address and name – when you subscribe to our newsletter.</p>

  <h3>b. Automatically Collected Information</h3>
  <p>Technical data, such as IP address, browser type, device information, and site interaction details.</p>
  <p>Cookies and local storage – used to support essential functionality and analytics (see Section 5).</p>

  <h2>3. What Is Personal Data?</h2>
  <p>
    "Personal data" refers to any information that can be used to identify an individual, directly or indirectly — such
    as your name, email address, IP address, or device identifiers.
  </p>

  <h2>4. How We Use Your Information</h2>
  <p>We use your data to:</p>
  <ul>
    <li>Send AI-generated news digests (only if you subscribe).</li>
    <li>Improve website performance and user experience through internal analytics.</li>
    <li>Ensure website security and protect against malicious activity.</li>
  </ul>
  <p>We do not use your information for marketing purposes or share it with advertisers.</p>

  <h2>5. Legal Basis for Processing</h2>
  <p>Under the General Data Protection Regulation (GDPR), we process your data based on:</p>
  <ul>
    <li>Your consent – for example, when you subscribe to our newsletter.</li>
    <li>
      Legitimate interest – such as ensuring website security and collecting privacy-compliant analytics data that does
      not personally identify users.
    </li>
  </ul>

  <h2>6. Cookies and Local Storage</h2>
  <p>
    We use a minimal number of cookies and local storage entries, all of which are essential or functional. No marketing
    or cross-site tracking cookies are used.
  </p>

  <h3>a. Strictly Necessary</h3>
  <ul>
    <li>
      cf_clearance – set by Cloudflare to verify secure access and protect against malicious visitors. Expires after 1
      year.
    </li>
    <li>
      PostHog session/local storage keys – used to maintain internal analytics and session integrity (e.g.,
      ph_phc_*_posthog, *_window_id, *_primary_window_exists).
    </li>
  </ul>

  <h3>b. Functional</h3>
  <ul>
    <li>darkMode – stores your theme preference (light/dark mode). Expires after 1 year.</li>
  </ul>

  <h3>c. Analytics (Self-hosted and Anonymized)</h3>
  <ul>
    <li>
      ph_phc_*_posthog – used by PostHog, a self-hosted or privacy-compliant analytics tool. It collects only anonymized
      usage patterns and does not store any personally identifiable data.
    </li>
  </ul>

  <p>You can disable cookies via your browser settings. Please note that some functionality may be affected.</p>

  <h2>7. Data Storage and Security</h2>
  <p>
    We apply appropriate technical and organizational measures to protect your data. We store only the minimal
    information required and retain it only as long as necessary to fulfill the stated purposes.
  </p>
  <p>All analytics data is anonymized. We do not store or process sensitive personal data.</p>

  <h2>8. Your Rights Under GDPR</h2>
  <p>If you are located in the European Economic Area (EEA), you have the following rights:</p>
  <ul>
    <li>Request access to your personal data.</li>
    <li>Request correction or deletion of your data.</li>
    <li>Withdraw consent at any time (e.g., unsubscribe from our newsletter).</li>
    <li>Lodge a complaint with a data protection authority if you believe your rights are being violated.</li>
  </ul>
  <p>To exercise any of these rights, please contact us via email (see Section 11).</p>

  <h2>9. Third-Party Services</h2>
  <p>We rely on the following external services to maintain and protect the website:</p>
  <ul>
    <li>Cloudflare – for network protection, bot filtering, and performance optimization.</li>
    <li>
      PostHog – a self-hosted or privacy-compliant analytics platform used to analyze aggregate, anonymized usage data.
      No personal data is shared with external parties.
    </li>
  </ul>
  <p>We do not sell, trade, or share your data with third-party advertisers or external analytics providers.</p>

  <h2>10. Newsletter and Email Communication</h2>
  <p>
    By subscribing to our newsletter, you agree to receive AI-generated news summaries via email. You can unsubscribe at
    any time by clicking the link in any email. Your contact details will never be used for other purposes or shared
    with third parties.
  </p>

  <h2>11. Changes to This Privacy Policy</h2>
  <p>
    We may update this policy from time to time to reflect changes in the law, technology, or our practices. The latest
    version will always be available at:
  </p>
  <p class="not-prose">
    <a
      href="/privacy-policy"
      class="underline px-0 text-base text-gray-900 transition-colors hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-400"
      >https://summerry.com/privacy-policy</a
    >
  </p>

  <h2>12. Contact Us</h2>
  <p>If you have any questions about this Privacy Policy or how we handle your data, please contact us:</p>
  <p>
    Email:
    <a
      href="mailto:info.summerry@gmail.com"
      class="underline px-0 text-base text-gray-900 transition-colors hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-400"
      >info.summerry&#64;gmail.com</a
    >
  </p>
  <p>
    Website:
    <a
      href="/"
      class="underline px-0 text-base text-gray-900 transition-colors hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-400"
      >https://summerry.com</a
    >
  </p>
</div>
